import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { my_routes } from "../router/all_routes";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import EdtVersionModal from "./edt_version_modal";
import EdtParameterModal from "./edt_parameter_modal";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllEDT } from "../../api/edt";
import { toast } from "sonner";
import { Spin } from "antd";
import DashboardMenu from "../user/admin/dashboardMenu";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";

type ScheduleVersion = {
  id: string;
  versionNumber: number;
  createdAt: string;
  updatedAt?: string;
  isPublished: boolean;
};

type Schedule = {
  id: string;
  nom: string;
  startDate: string;
  endDate: string;
  versions: ScheduleVersion[];
};

// Bouton protégé pour creer un EDT
const ProtectedBtnAddEDT = withPermission(
  (props: any) => (
    <Link
      to={props.routes.timeTableManager}
      className="btn btn-primary btn-add-schedule"
    >
      <i className="fas fa-plus me-2"></i>Programmer un nouvel emploi du temps
    </Link>
  ),
  [buildPermission(Resource.EDT, Action.CREATE)],
  "masquer",
);

// Bouton protégé pour accéder aux paramètres tout court
const ProtectedBtnAccessSettings = withPermission(
  (props: any) => (
    <Dropdown>
      <Dropdown.Toggle variant="outline-primary" id="settings-dropdown">
        <i className="fas fa-cog me-2"></i>Paramètres
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <ProtectedBtnAccessEDTParams setShowSettings={props.setShowSettings} />
        <ProtectedBtnAccessCourse
          routes={props.routes}
          navigate={props.navigate}
        />
        <ProtectedBtnAccessEvent
          routes={props.routes}
          navigate={props.navigate}
        />
      </Dropdown.Menu>
    </Dropdown>
  ),
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.SETTING_EDT, action),
    ),
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.COURSE, action),
    ),
  ],
  "masquer",
);

// Bouton protégé pour accéder aux paramètres d'un EDT
const ProtectedBtnAccessEDTParams = withPermission(
  (props: any) => (
    <Dropdown.Item onClick={() => props.setShowSettings(true)}>
      <i className="fas fa-calendar-alt me-2"></i>Année universitaire
    </Dropdown.Item>
  ),
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.SETTING_EDT, action),
    ),
  ],
  "masquer",
);

// Bouton protégé pour accéder aux cours
const ProtectedBtnAccessCourse = withPermission(
  (props: any) => (
    <Dropdown.Item onClick={() => props.navigate(props.routes.coursList)}>
      <i className="fas fa-book me-2"></i>Gestion des cours
    </Dropdown.Item>
  ),
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.COURSE, action),
    ),
  ],
  "masquer",
);
const ProtectedBtnAccessEvent = withPermission(
  (props: any) => (
    <Dropdown.Item onClick={() => props.navigate(props.routes.eventPage)}>
      <i className="fas fa-calendar me-2"></i>Gestion des évenements
    </Dropdown.Item>
  ),
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.EVENT, action),
    ),
  ],
  "masquer",
);
const EdtList = () => {
  const { selectedYear } = useAcademicYear();
  const routes = my_routes;
  const navigate = useNavigate();

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null,
  );
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchAllEdtByYear = async () => {
    try {
      setIsLoading(true);
      const response = await getAllEDT(selectedYear?.id);
      // console.log("response data: ", response);
      const formattedEDT = response.data.map((item: any) => {
        const formattedVersionEDT = item?.VersionEDTs
          ? item?.VersionEDTs.map((version: any) => ({
              id: version?.id || "",
              versionNumber: version?.version_edt || "",
              createdAt: item?.createdAt || "",
              isPublished: version?.state_edt === "Publiée",
            }))
          : [];
        // console.log("item", item);
        return {
          id: item?.id || "",
          nom: item?.title || "",
          startDate:
            new Date(item?.valid_from).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }) || "2023-10-15",
          endDate:
            new Date(item?.valid_until).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }) || "2023-12-15",
          versions: formattedVersionEDT,
        };
      });
      // console.log("formattedEDT: ", formattedEDT);
      setSchedules([...formattedEDT]);

      // mettre à jour selectedSchedule si il existe déjà dans la nouvelle liste après le fetch
      if (selectedSchedule) {
        const updatedSelected = formattedEDT.find(
          (schedule: Schedule) => schedule.id === selectedSchedule.id,
        );
        setSelectedSchedule(updatedSelected || null);
      }
    } catch (error: any) {
      // console.log("erreur de chargement des Emplois du temps : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedYear?.id) return;
    fetchAllEdtByYear();
  }, [selectedYear]);

  // Fonction pour filtrer les emplois du temps
  const filteredSchedules = schedules.filter((schedule) =>
    schedule.nom.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Fonction pour gérer le clic sur un emploi du temps
  const handleScheduleClick = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
  };

  // Fonction pour fermer le modal des versions
  const closeVersionsModal = () => {
    setSelectedSchedule(null);
  };

  // Fonction pour naviguer vers la page de détails d'une version
  const viewVersionDetails = (scheduleId: string, versionId: string) => {
    navigate(
      `${routes.timeTableManager}?id=${scheduleId}&version=${versionId}`,
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {!selectedYear?.statut && <InfosAlertYearDisabled />}

        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between">
          {/* <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Emplois du Temps</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Application</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Emplois du temps
                </li>
              </ol>
            </nav>
          </div> */}

          <DashboardMenu />
        </div>
        {/* /Page Header */}

        {/* Search Filter */}
        <div className="row mb-3">
          {/* Toolbar */}
          <div className="toolbar-container d-flex gap-2 flex-wrap ">
            <ProtectedBtnAccessSettings
              routes={routes}
              navigate={navigate}
              setShowSettings={setShowSettings}
            />
            <ProtectedBtnAddEDT routes={routes} />
            <div className="form-group w-50">
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher un emploi du temps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* /Search Filter */}

        {/* Schedule List */}
        <div className="row">
          <div className="col-sm-12">
            {isLoading ? (
              <div className="text-center py-5">
                <Spin size="large" />
              </div>
            ) : (
              <div className="card card-table">
                <div className="card-body">
                  <div
                    className="table-responsive overflow-auto d-block"
                    style={{ height: "calc(95vh - 280px)" }}
                  >
                    <table className="table table-hover table-center mb-0 position-relative">
                      <thead className="position-sticky top-0 bg-white">
                        <tr>
                          <th></th>
                          <th>Nom</th>
                          <th>Période</th>
                          <th>Versions</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSchedules.length > 0 ? (
                          filteredSchedules.map((schedule) => {
                            const hasPublished = schedule.versions.some(
                              (v) => v.isPublished,
                            );
                            return (
                              <tr
                                key={schedule.id}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleScheduleClick(schedule)}
                              >
                                <td>
                                  <i
                                    className="fas fa-check-circle"
                                    style={{
                                      color: hasPublished ? "green" : "gray",
                                      fontSize: "1.2em",
                                    }}
                                    title={
                                      hasPublished
                                        ? "Version publiée"
                                        : "Aucune version publiée"
                                    }
                                  />
                                </td>
                                <td>{schedule.nom}</td>
                                <td>
                                  {schedule.startDate} à {schedule.endDate}
                                </td>
                                <td>
                                  <span className="badge badge-soft-info d-inline-flex align-items-center mb-1">
                                    <i className="ti ti-circle-filled fs-5 me-1" />
                                    {schedule.versions.length} version(s)
                                  </span>
                                </td>
                                <td>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>
                                        {hasPublished
                                          ? "Version publiée"
                                          : "Aucune version publiée"}
                                      </Tooltip>
                                    }
                                  >
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      disabled={!hasPublished}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Empêche l'ouverture du modal
                                        viewVersionDetails(
                                          schedule.id,
                                          schedule.versions.find(
                                            (v) => v.isPublished,
                                          )?.id || "",
                                        );
                                      }}
                                    >
                                      <i
                                        className="fas fa-eye"
                                        style={{ cursor: "pointer" }}
                                      />{" "}
                                      voir
                                    </button>
                                  </OverlayTrigger>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center">
                              Aucun emploi du temps trouvé
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* /Schedule List */}

        {/* Modal pour afficher les versions */}
        {selectedSchedule && (
          <EdtVersionModal
            show={!!selectedSchedule}
            selectedSchedule={selectedSchedule}
            onClose={closeVersionsModal}
            onSaved={() => fetchAllEdtByYear()}
          />
        )}
        {/* Modal pour afficher les paramètres */}
        <EdtParameterModal
          showSettings={showSettings}
          onHide={() => setShowSettings(false)}
        />
      </div>
    </div>
  );
};

export default EdtList;
